/**
 * Assemble the CMS v2 conversion `trackingContext` for the generic
 * `navigation.linkout` GET redirect (Linkout + Hosted Lead Form offerings on
 * rankings / EL pages).
 *
 * This is the redirect-path analog of the Voyager POST's
 * {@link ../buildConversionCreationRequest.buildConversionCreationRequest}
 * `trackingContext`: it reads the SAME live Cohesion identity
 * (`anonymousId` / `sessionId` / `instanceId` / `page` / `userAgent`) so the
 * BFF reconstructs an attributable conversion instead of relying on the
 * unreliable `x-rvedu-*` headers the client→BFF hop drops. The result is
 * serialized to URL-encoded JSON by `buildConversionRedirectUrl` and parsed
 * server-side.
 *
 * `formatType` / `formatSubtype` are load-bearing — CMS's
 * `ConversionTrackedMetaEvent` uses them for monetization classification, so
 * they are threaded through verbatim (with the same `dexp-offering` default the
 * Voyager path uses when the caller omits `formatType`).
 *
 * Returns `undefined` when nothing is resolvable (SSR, Cohesion not yet ready,
 * no classification), so `buildConversionRedirectUrl` omits the param cleanly
 * and the BFF falls back to header-derived identity. Defensive by design: any
 * failure returns `undefined` rather than throwing into the click handler.
 */

/** Default `formatType` — mirrors the Voyager POST conversion path. */
export const DEFAULT_LINKOUT_FORMAT_TYPE = 'dexp-offering';

export interface LinkoutTrackingContextInput {
  /**
   * Tenant tagular `apiKey` (a UUID) resolved by the host from `siteConfig`.
   * CMS validates `trackingContext.tenantId` as `@IsUUID()`, so the host passes
   * the already-validated apiKey (NOT the tenant slug) or omits it.
   */
  trackingTenantId?: string;
  /** Monetization `formatType`; defaults to {@link DEFAULT_LINKOUT_FORMAT_TYPE}. */
  formatType?: string;
  /** Monetization `formatSubtype` (e.g. `dexp-results` / `dexp-unqualified`). */
  formatSubtype?: string;
}

export function buildLinkoutTrackingContext(
  input: LinkoutTrackingContextInput = {},
): Record<string, unknown> | undefined {
  try {
    const cohesion =
      typeof window !== 'undefined' ? window._Cohesion : undefined;
    const cohesionWebContext = cohesion?.webContext;

    const anonymousId =
      cohesion?.anonymousId || cohesionWebContext?.anonymousId;
    const instanceId = cohesion?.instanceId || cohesionWebContext?.instanceId;
    const sessionId = cohesion?.sessionId || cohesionWebContext?.sessionId;
    const referrer =
      cohesionWebContext?.page?.referrer ||
      (typeof document !== 'undefined' ? document.referrer : '') ||
      '';
    const title =
      cohesionWebContext?.page?.title ||
      (typeof document !== 'undefined' ? document.title : '') ||
      '';
    const url =
      cohesionWebContext?.page?.url ||
      (typeof window !== 'undefined' ? window.location?.href : '') ||
      '';
    const userAgent =
      cohesionWebContext?.userAgent ||
      (typeof window !== 'undefined' ? window.navigator?.userAgent : '') ||
      '';
    const writeKey = cohesion?.tagular?.writeKey || cohesion?.writeKey;

    const page: Record<string, string> = {};
    if (referrer) page.referrer = referrer;
    if (title) page.title = title;
    if (url) page.url = url;

    // `fromManual: false` matches the Voyager POST conversion shape; the field
    // is always meaningful (server-initiated, not a manual retry).
    const webContext: Record<string, unknown> = { fromManual: false };
    if (anonymousId) webContext.anonymousId = anonymousId;
    if (instanceId) webContext.instanceId = instanceId;
    if (sessionId) webContext.sessionId = sessionId;
    if (Object.keys(page).length > 0) webContext.page = page;
    if (userAgent) webContext.userAgent = userAgent;

    const formatType = input.formatType?.trim() || DEFAULT_LINKOUT_FORMAT_TYPE;
    const formatSubtype = input.formatSubtype?.trim();
    const trackingTenantId = input.trackingTenantId?.trim();

    const trackingContext: Record<string, unknown> = {
      ...(trackingTenantId ? { tenantId: trackingTenantId } : {}),
      formatType,
      ...(formatSubtype ? { formatSubtype } : {}),
      ...(writeKey ? { writeKey } : {}),
      webContext,
    };

    // Only the always-present `formatType` + `fromManual` resolved: no live
    // identity is available (SSR / Cohesion not ready). Skip so the redirect
    // degrades to the header-derived path rather than shipping an empty shell.
    const hasIdentity =
      Boolean(anonymousId) ||
      Boolean(sessionId) ||
      Boolean(instanceId) ||
      Object.keys(page).length > 0 ||
      Boolean(userAgent);
    if (!hasIdentity) {
      return undefined;
    }

    return trackingContext;
  } catch {
    return undefined;
  }
}
